<script setup lang="ts">
    import { computed, inject, onMounted, reactive, ref, toRaw } from 'vue';
    import ConfirmationModal from '@/components/ConfirmationModal.vue';
    import ToastNotification from '@/components/ToastNotification.vue';
    import axios from 'axios';
    import { formatDate } from '../../../utils';
    import { useRoute } from 'vue-router';
    import CustomPagination from '@/components/CustomPagination.vue';

    const BACKEND_URL = inject('BACKEND_URL');
    const route = useRoute();
    const errorMessage = ref('');
    const loading = ref(false);
    const loadingInvitations = ref(false);
    const toastMessage = ref('');
    const toastType = ref<'success' | 'error' | 'info' | 'warning'>('success');
    const showToast = ref(false);
    const itemsList = ref([] as any[]);
    const confirmationModalRef = ref<InstanceType<typeof ConfirmationModal> | null>(null);
    const selectedItems = ref([] as any[]);
    const areAllItemsSelected = computed(() => {
        return itemsList.value.length == selectedItems.value.length;
    });
    const startupInfos = reactive({
        collectivites: [] as any[],
        pays: [] as any[]
    });
    const queryParams = reactive({
        pageNumber: 1,
        sortDir: 'ASC',
        sortColumn: 'fullname',
        sortOption: 'fullname_asc',
        borneInf: '',
        borneSup: '',
        pays_id: '',
        collectivite_id: '',
        search: '',
        trashed: 0
    });
    const pagination = reactive({
        total: 0,
        per_page: 10,
        last_page: 1
    });
    const SORT_OPTIONS = [
        { value: 'fullname_asc', label: 'Nom & Prénoms (ASC)', sort_column: 'fullname', sort_direction: 'ASC' },
        { value: 'fullname_desc', label: 'Nom & Prénoms (DESC)', sort_column: 'fullname', sort_direction: 'DESC' },
        { value: 'created_at_asc', label: 'Date (ASC)', sort_column: 'created_at', sort_direction: 'ASC' },
        { value: 'created_at_desc', label: 'Date (DESC)', sort_column: 'created_at', sort_direction: 'DESC' },
    ];
    const showAdvancedFilters = ref(false);

    onMounted(async () => {
        // Charger les collectivités pour le filtre
        try {
            const collectivitesResponse = await axios.get('/backoffice/collectivites/list?pageSize=1000');
            startupInfos.collectivites = collectivitesResponse.data.data || [];
        } catch (error: any) {
            console.error('Erreur lors du chargement des collectivités:', error);
        }

        // Lire collectivite_id depuis les query params
        const collectiviteIdParam = route.query.collectivite_id as string;
        if (collectiviteIdParam) {
            queryParams.collectivite_id = collectiviteIdParam;
        }
        
        doListItems();
    });

    async function doListItems() {
        try {
            loading.value = true;
            errorMessage.value = '';
            const queryParamsRaw = toRaw(queryParams);
            const queryString = new URLSearchParams(Object.fromEntries(Object.entries(queryParamsRaw).filter(([k,v]) => v !== '').map(([k,v]) => [k, String(v)]))).toString();
            const httpResponse = await axios.get(`/backoffice/prospects/list?${queryString}`);
            itemsList.value = httpResponse.data.data || [];
            pagination.total = httpResponse.data.total || 0;
            pagination.per_page = httpResponse.data.per_page || 10;
            pagination.last_page = httpResponse.data.last_page || 1;
        } catch (error: any) {
            errorMessage.value = error.message || 'Erreur lors du chargement des prospects';
            itemsList.value = [];
        } finally {
            loading.value = false;
        }
    }

    function onPaginate(pageNumber: number) {
        queryParams.pageNumber = pageNumber;
        doListItems();
    }

    function handleCheckAll(_event: any) {
        const checked = _event.target.checked;
        if (checked) {
            selectedItems.value = itemsList.value.map(i => i.id);
        } else {
            selectedItems.value = [];
        }
    }

    function toggleAdvancedFilters() {
        showAdvancedFilters.value = !showAdvancedFilters.value;
    }

    function onFiltersChanged() {
        queryParams.pageNumber = 1;
        doListItems();
    }

    function onSortChanged() {
        // Convertir sortOption en sortColumn et sortDir
        const option = queryParams.sortOption;
        if (option === 'fullname_asc') {
            queryParams.sortColumn = 'fullname';
            queryParams.sortDir = 'ASC';
        } else if (option === 'fullname_desc') {
            queryParams.sortColumn = 'fullname';
            queryParams.sortDir = 'DESC';
        } else if (option === 'created_at_asc') {
            queryParams.sortColumn = 'created_at';
            queryParams.sortDir = 'ASC';
        } else if (option === 'created_at_desc') {
            queryParams.sortColumn = 'created_at';
            queryParams.sortDir = 'DESC';
        }
        queryParams.pageNumber = 1;
        doListItems();
    }

    function getStatusTags(item: any) {
        const tags = [];
        if (item.email_verified_at) {
            tags.push({ label: 'Authentification', type: 'success' });
        } else {
            tags.push({ label: 'Authentification', type: 'warning' });
        }
        
        if (item.souscription_id) {
            tags.push({ label: 'Souscription', type: 'success' });
        } else {
            tags.push({ label: 'Souscription', type: 'warning' });
        }
        
        if (item.code_securite) {
            tags.push({ label: 'Code de sécurité', type: 'success' });
        } else {
            tags.push({ label: 'Code de sécurité', type: 'warning' });
        }
        
        if (item.actif) {
            tags.push({ label: 'Actif', type: 'success' });
        } else {
            tags.push({ label: 'Inactif', type: 'warning' });
        }
        
        return tags;
    }

    function formatDateFrench(dateString: string | null): string {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        const monthsFr = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
        return `${date.getDate()} ${monthsFr[date.getMonth()]} ${date.getFullYear()}`;
    }

    async function sendInvitation(prospect: any) {
        try {
            loadingInvitations.value = true;
            const response = await axios.post('/backoffice/users/send_confirmation_email', {
                user_id: prospect.id
            });
            showToast.value = true;
            toastMessage.value = 'Invitation envoyée avec succès';
            toastType.value = 'success';
            doListItems();
        } catch (error: any) {
            showToast.value = true;
            toastMessage.value = error.response?.data?.message || error.message || "Erreur lors de l'envoi de l'invitation";
            toastType.value = 'error';
        } finally {
            loadingInvitations.value = false;
        }
    }
</script>

<template>
    <section class="container-full-app bg-grey-50">
        <div class="page-space-30"></div>

        <!--*** Widget *** -->
        <section class="s20">
            <div class="widget shadow-100 s20 pb-2">
                <!--Card-->
                <div class="container-full-2 s20 my-5">
                    <div class="s7"><h4 class="font-semibold mt-4">Prospects <span class="text-sm text-grey-500">(recent)</span></h4></div>
                    <div class="s13 flex justify-content-right">
                        <div class="form-field-floating default sm s8_mobile ml-3">
                            <select class="form-input-select" name="collectivite" id="collectivite" v-model="queryParams.collectivite_id" @change="onFiltersChanged">
                                <option value="">Toutes les collectivités</option>
                                <option :value="col.id" v-for="col in startupInfos.collectivites" :key="col.id">{{ col.nom }}</option>
                            </select>
                        </div>
                        <div class="form-field-floating default sm s4_mobile ml-3">
                            <select class="form-input-select" name="sort" id="sort" v-model="queryParams.sortOption" @change="onSortChanged">
                                <option value="fullname_asc">Nom & Prénoms (ASC)</option>
                                <option value="fullname_desc">Nom & Prénoms (DESC)</option>
                                <option value="created_at_asc">Date (ASC)</option>
                                <option value="created_at_desc">Date (DESC)</option>
                            </select>
                        </div>
                        <button @click="toggleAdvancedFilters" class="btn normal white2 s3_mobile ml-4 justify-content-center btn-filtre btn-filter">
                            <img src="/icons/icon-filter.svg" class="btn-icon" alt="" />
                            <span>Options</span>
                        </button>
                    </div>
                    <div class="clear-both"></div>
                    <!--Options-->
                    <div class="custom-filter-set-area" v-show="showAdvancedFilters">
                        <div class="grid-filter6 mt-4 mb-0">
                            <div class="form-field-floating reactive sm">
                                <input type="date" class="form-input" name="date1" id="date1" v-model="queryParams.borneInf" @change="onFiltersChanged" placeholder="">
                                <label for="date1">Date de création &lt;=</label>
                            </div>
                            <div class="form-field-floating reactive sm">
                                <input type="date" class="form-input" name="date2" id="date2" v-model="queryParams.borneSup" @change="onFiltersChanged" placeholder="">
                                <label for="date2">Date de création &gt;=</label>
                            </div>
                            <div class="form-field-floating reactive sm">
                                <input type="text" class="form-input" name="search" id="search" v-model="queryParams.search" @input="onFiltersChanged" placeholder="">
                                <label for="search">Recherche</label>
                            </div>
                        </div>
                    </div>
                    <!---->
                </div>
            </div>

            <toast-notification 
                :message="toastMessage" 
                :type="toastType" 
                :show="showToast" 
                @update:show="showToast = $event"
            />

            <div v-if="errorMessage" class="alert alert-danger mb-4">{{ errorMessage }}</div>

            <div v-if="loading && itemsList.length === 0" class="flex-center py-10">
                <div class="loader dark"></div>
            </div>

            <div v-else>
                <table class="table separator skin3 selection rounded">
                    <thead>
                        <tr>
                            <th>
                                <label class="btn-circle" for="check_all">
                                    <input type="checkbox" id="check_all" :checked="areAllItemsSelected" @change="handleCheckAll" />
                                </label>
                            </th>
                            <th></th>
                            <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Information</th>
                            <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Utilisation</th>
                            <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Collectivité</th>
                            <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Statut</th>
                            <th class="flex-right"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-if="itemsList.length === 0">
                            <tr>
                                <td colspan="7" class="text-center text-grey-500 py-10">Aucun prospect trouvé</td>
                            </tr>
                        </template>
                        <tr class="text-grey-700 over-effect" v-for="(item, index) in itemsList" :key="'item_' + index">
                            <td class="selection-item">
                                <label class="btn-circle" :for="'item_check_' + index">
                                    <input type="checkbox" name="selectedItems[]" :id="'item_check_' + index" :value="item.id" v-model="selectedItems" />
                                </label>
                            </td>
                            <td class="flex">
                                <div class="square-xl rounded-15" style="background-color: rgb(var(--color-warning-rgb), 0.2);">
                                    <img :src="!!item.photo_url ? item.photo_url : '/assets/users_avatar/600w/user.jpg'" width="100%" />
                                </div>
                            </td>
                            <td class="text-xl font-semibold" style="width: 15%;">
                                {{ item.nom }} {{ item.prenom }}
                                <p class="text-sm text-grey-600 mt-1">{{ item.email }}</p>
                                <p class="text-sm text-grey-600">{{ item.telephone }}</p>
                            </td>
                            <td class="text-3xl">
                                {{ item.quotite_cessible_pourcent ? item.quotite_cessible_pourcent + '%' : '-' }}
                                <p class="mt-1 text-sm">{{ item.quotite_cessible_montant ? new Intl.NumberFormat('fr-FR').format(item.quotite_cessible_montant) + ' F' : '-' }}</p>
                            </td>
                            <td>
                                {{ item.collectivite?.nom || 'N/A' }}
                            </td>
                            <td>
                                <p class="text-xl font-semibold mb-2">
                                    <span class="tag-state warning stroke">En attente</span>
                                </p>
                                <template v-if="item.created_at">
                                    Invitation envoyée le {{ formatDateFrench(item.created_at) }}<br class="mb-5" />
                                </template>
                                <template v-for="(tag, tagIndex) in getStatusTags(item)" :key="tagIndex">
                                    <span :class="'tag-state ' + tag.type + ' stroke'">{{ tag.label }}</span>
                                    <span v-if="tagIndex < getStatusTags(item).length - 1">&nbsp;</span>
                                </template>
                            </td>
                            <td>
                                <div class="flex-right gap-4">
                                    <router-link :to="'/app/users/form/' + item.id">
                                    <button class="btn tips">
                                        <img src="/icons/icon-edit.svg" class="btn-icon" alt="" />
                                        <span>Editer</span>
                                    </button>
                                    </router-link>
                                    <button class="btn white2 active" @click="sendInvitation(item)" :disabled="loadingInvitations">
                                        <img src="/icons/icon-send.svg" class="btn-icon" alt="" />
                                        <span>Inviter</span>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <!--Pagination-->
                <div style="display: flex; flex-direction: row; justify-content: center;">
                    <custom-pagination v-model="queryParams.pageNumber" :total="pagination.total" :per-page="pagination.per_page" :last_page="pagination.last_page" @update:modelValue="onPaginate" />
                </div>
                <!---->
            </div>
            <div class="clear-both h-30"></div>
            
        </section>
        
        <div class="clear-both h-30"></div>
        <confirmation-modal ref="confirmationModalRef"></confirmation-modal>
    </section>
</template>
